import {
  appendFeeLines,
  buildFeeLines,
  normalizeFees,
  orderNormalizedFees,
  percentStr,
  toId,
  NormalizedFee,
} from 'QuoteApp/utils/feeStrategy';
import {
  Fee,
  FeeLine,
  FeeStrategyEnum,
} from 'QuoteApp/services/endpoints/FeeStrategy';
import { formatFeeAmount, formatPrice } from 'Shared/utils';
import {
  PaymentFrequencyEnum,
  PaymentPlanType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';
import { FeeStrategyOption } from 'QuoteApp/components/FeeStrategy/FeeStrategyPicker/types';
import { PaymentInstallment } from 'QuoteApp/components/FeeStrategy/InstallmentNavigator/types';

export const getPaymentPlanByStrategyName = (
  paymentPlans: PaymentPlanType[],
  strategyName: FeeStrategyEnum
): PaymentPlanType | undefined =>
  paymentPlans.find((plan) => plan.feeStrategy.name === strategyName);

export const getPaymentPlansByFrequency = (
  paymentPlans: PaymentPlanType[],
  frequency: PaymentFrequencyEnum
): PaymentPlanType[] =>
  paymentPlans.filter((plan) => plan.paymentFrequencyIdentifier === frequency);

export const getFeesByStrategyName = (
  paymentPlans: PaymentPlanType[],
  strategyName: FeeStrategyEnum
): Fee[] => {
  const paymentPlansFiltered = paymentPlans.filter(
    (plan) => plan.feeStrategy.name === strategyName
  );

  if (paymentPlansFiltered.length === 0) {
    return [];
  }

  return paymentPlansFiltered[0].feeStrategy.fees;
};

export const getFeeValueByName = (fees: Fee[], name: string): string => {
  const normalizedFees = normalizeFees(fees);
  const fee = normalizedFees.find((fee) => fee.id === toId(name));

  if (!fee) {
    return '';
  }

  if (fee.kind === 'FLAT') {
    return formatFeeAmount(fee.amount || 0);
  }

  if (fee.kind === 'PERCENT') {
    return percentStr(fee.percent || 0);
  }

  return '';
};

export const buildPredictableFeeLines = (
  predictableFees: Fee[],
  variableFees: Fee[],
  isPayInFull: boolean = false
) => {
  const normalizedPredictableFees = normalizeFees(predictableFees);
  const normalizedVariableFees = normalizeFees(variableFees);

  const orderedNormalizedFees = orderNormalizedFees(normalizedPredictableFees, [
    'installment',
    'underwriting_fee',
    'late_payment',
    'insufficient_funds',
    'reinstatement',
    'fr_filing',
  ]);
  const orderedNormalizedVariableFees = orderNormalizedFees(
    normalizedVariableFees,
    [
      'installment',
      'underwriting_fee',
      'late_payment',
      'insufficient_funds',
      'reinstatement',
      'fr_filing',
    ]
  );

  const predictableFeeLines = buildFeeLines(orderedNormalizedFees, {});
  const variableFormatters: Record<string, (fee: NormalizedFee) => FeeLine> = {
    installment: (fee) => ({
      label: fee.label,
      value: '$0',
      altValue:
        fee.kind === 'FLAT'
          ? formatFeeAmount(fee.amount || 0)
          : fee.kind === 'PERCENT'
            ? percentStr(fee.percent || 0) + ' simple interest'
            : '',
    }),
    underwriting_fee: (fee) => ({
      label: fee.label,
      value: '$0',
      altValue:
        fee.kind === 'FLAT'
          ? formatFeeAmount(fee.amount || 0) + ' annually'
          : '',
    }),
    late_payment: (fee) => ({
      label: fee.label,
      value: '$0',
      altValue:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
    insufficient_funds: (fee) => ({
      label: fee.label,
      value: '$0',
      altValue:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
    reinstatement: (fee) => ({
      label: fee.label,
      value: '$0',
      altValue:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
    fr_filing: (fee) => ({
      label: fee.label + ' (e.g. SR-22)',
      value: '$0',
      altValue:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
  };

  // Exclude FL installment fee when paying in full
  if (isPayInFull) {
    delete variableFormatters.installment;
  }

  const variableFeeLines = buildFeeLines(
    orderedNormalizedVariableFees,
    variableFormatters
  );

  const combined = [...predictableFeeLines, ...variableFeeLines];

  return appendFeeLines(combined, [
    { label: 'Save up to 25% off vehicle maintenance', value: 'Included' },
    { label: 'Maintenance reminders', value: 'Included' },
    { label: 'Chat with expert mechanics', value: 'Included' },
  ]);
};

export const buildVariableFeeLines = (
  fees: Fee[],
  isPayInFull: boolean = false
) => {
  const normalizedVariableFees = normalizeFees(fees);
  const orderedNormalizedFees = orderNormalizedFees(normalizedVariableFees, [
    'installment',
    'underwriting_fee',
    'late_payment',
    'insufficient_funds',
    'reinstatement',
    'fr_filing',
  ]);

  const formatters: Record<string, (fee: NormalizedFee) => FeeLine> = {
    installment: (fee) => ({
      label: fee.label,
      value:
        fee.kind === 'FLAT'
          ? formatFeeAmount(fee.amount || 0)
          : fee.kind === 'PERCENT'
            ? percentStr(fee.percent || 0) + ' simple interest (see below)'
            : '',
    }),
    underwriting_fee: (fee) => ({
      label: fee.label,
      value:
        fee.kind === 'FLAT'
          ? formatFeeAmount(fee.amount || 0) + ' annually'
          : '',
    }),
    late_payment: (fee) => ({
      label: fee.label,
      value:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
    insufficient_funds: (fee) => ({
      label: fee.label,
      value:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
    reinstatement: (fee) => ({
      label: fee.label,
      value:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
    fr_filing: (fee) => ({
      label: fee.label + ' (e.g. SR-22)',
      value:
        fee.kind === 'FLAT' ? formatFeeAmount(fee.amount || 0) + ' per' : '',
    }),
  };

  // Exclude FL installment fee when paying in full
  if (isPayInFull) {
    delete formatters.installment;
  }

  const variableFeeLines = buildFeeLines(orderedNormalizedFees, formatters);

  return appendFeeLines(variableFeeLines, [
    { label: 'Save up to 25% off vehicle maintenance', value: 'Not Included' },
    { label: 'Maintenance reminders', value: 'Not Included' },
    { label: 'Chat with expert mechanics', value: 'Not Included' },
  ]);
};

export const buildVariableInstallments = (
  paymentSchedule: PaymentPlanType['paymentSchedule']
): PaymentInstallment[] => {
  const installmentEntries = paymentSchedule.filter(
    (entry) => entry.type === 'INSTALLMENT'
  );

  const installments: PaymentInstallment[] = installmentEntries.map((entry) => {
    const installmentFee = entry.items.find(
      (item) => item.name.toLowerCase() === 'installment'
    )!;

    return {
      amount: formatPrice(installmentFee.amount),
      date: entry.date,
    };
  });

  return installments;
};

export const buildPredictableStrategyOption = (
  feeLines: FeeLine[],
  feePerInstallment: string,
  isPayInFull: boolean
): FeeStrategyOption => ({
  strategyName: 'predictable',
  displayName: 'Predictable Protection Fee',
  feeNote: isPayInFull
    ? ''
    : `Predictable Protection Fee: ${feePerInstallment} per payment`,
  feeLines,
  buttonLabel: {
    selected: 'Selected',
    unselected: 'Choose predictable protection fee',
  },
  installments: null,
});

export const buildVariableStrategyOption = (
  feeLines: FeeLine[],
  feeInterestRate: string,
  isPayInFull: boolean,
  paymentSchedule: PaymentPlanType['paymentSchedule'] = [],
  isSimpleInterest = false
): FeeStrategyOption => ({
  strategyName: 'variable',
  displayName: 'Variable Fees',
  feeNote:
    isPayInFull || !isSimpleInterest
      ? ''
      : `${feeInterestRate} simple interest Payment Installment Fee schedule`,
  feeLines,
  buttonLabel: {
    selected: 'Selected',
    unselected: 'Choose variable fees',
  },
  installments:
    isPayInFull || !isSimpleInterest
      ? null
      : buildVariableInstallments(paymentSchedule),
});
